<template>
    <transition name="welcome-fade">
        <div v-if="visible" class="welcome-transition-overlay">
            <div class="welcome-content">
                <div class="logo-container">
                    <img src="https://static.supsis.live/common/supsisAiLogo.png" :alt="$t('message.supsisAiLogoAlt')" class="welcome-logo" />
                </div>
                <div class="messages-container">
                    <h1 class="welcome-title">
                        <span class="typing-text">{{ displayedTitle }}</span><span v-if="isTypingTitle" class="cursor">|</span>
                    </h1>
                    <h2 class="welcome-message">
                        <span class="typing-text colored-text">{{ displayedMessage }}</span><span v-if="isTypingMessage" class="cursor">|</span>
                    </h2>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters } from "vuex";

export default {
    name: "WelcomeTransition",
    data() {
        return {
            visible: false,
            displayedTitle: "",
            displayedMessage: "",
            typingSpeed: 20,
            typingTimer: null,
            messageTimer: null,
            isTypingTitle: false,
            isTypingMessage: false,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
        }),
        ...mapGetters({
            isLoggedIn: "auth/isLoggedIn",
        }),
        greetingMessage() {
            const greeting = this.getGreetingByTime();
            const emoji = this.getGreetingEmoji();
            if (!this.user || !this.user.name) {
                return `${greeting} ${emoji}`;
            }
            const userName = this.user.name.split(" ")[0];
            return `${greeting}, ${userName} ${emoji}`;
        },
        localizedTitleText() {
            return this.$t("login.welcomeTitle") + " 👋";
        },
    },
    methods: {
        getGreetingByTime() {
            const hour = moment().hour();
            if (hour >= 5 && hour < 12) {
                return this.$t("login.welcomeMorning");
            } else if (hour >= 12 && hour < 17) {
                return this.$t("login.welcomeAfternoon");
            } else if (hour >= 17 && hour < 22) {
                return this.$t("login.welcomeEvening");
            } else {
                return this.$t("login.welcomeNight");
            }
        },
        getGreetingEmoji() {
            const hour = moment().hour();
            if (hour >= 5 && hour < 12) {
                return "☀️";
            } else if (hour >= 12 && hour < 17) {
                return "🌤️";
            } else if (hour >= 17 && hour < 22) {
                return "🌙";
            } else {
                return "⭐";
            }
        },
        typeText(text, callback) {
            let index = 0;
            const self = this;
            const isTitle = callback === this.updateTitle;
            
            if (this.typingTimer) {
                clearInterval(this.typingTimer);
            }
            
            if (isTitle) {
                this.isTypingTitle = true;
            } else {
                this.isTypingMessage = true;
            }
            
            this.typingTimer = setInterval(function() {
                if (index < text.length) {
                    callback.call(self, text.substring(0, index + 1));
                    index++;
                } else {
                    clearInterval(self.typingTimer);
                    self.typingTimer = null;
                    if (isTitle) {
                        self.isTypingTitle = false;
                        setTimeout(function() {
                            self.startTypingMessage();
                        }, 500);
                    } else {
                        self.isTypingMessage = false;
                    }
                }
            }, this.typingSpeed);
        },
        updateTitle(text) {
            this.displayedTitle = text;
        },
        updateMessage(text) {
            this.displayedMessage = text;
        },
        startTypingTitle() {
            this.displayedTitle = "";
            this.isTypingTitle = true;
            this.typeText(this.localizedTitleText, this.updateTitle);
        },
        startTypingMessage() {
            const message = this.greetingMessage;
            this.displayedMessage = "";
            this.isTypingMessage = true;
            this.typeText(message, this.updateMessage);
        },
        shouldShowWelcome() {
            const storageKey = "welcome-transition-shown-date";
            const today = moment().format("YYYY-MM-DD");
            const lastShownDate = localStorage.getItem(storageKey);

            if (lastShownDate === today) {
                return false;
            }

            localStorage.setItem(storageKey, today);
            return true;
        },
        showWelcome() {
            if (!this.shouldShowWelcome()) {
                return;
            }

            if (!this.isLoggedIn || !this.user) {
                return;
            }

            this.visible = true;
            this.displayedTitle = "";
            this.displayedMessage = "";

            setTimeout(() => {
                this.startTypingTitle();
            }, 500);

            const messageLength = this.greetingMessage.length;
            const totalTypingTime = (this.localizedTitleText.length + messageLength) * this.typingSpeed + 2000;
            setTimeout(() => {
                this.visible = false;
            }, totalTypingTime);
        },
    },
    watch: {
        isLoggedIn(newValue) {
            if (newValue && this.user) {
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.showWelcome();
                    }, 300);
                });
            }
        },
        user: {
            handler(newValue) {
                if (newValue && this.isLoggedIn) {
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.showWelcome();
                        }, 300);
                    });
                }
            },
            immediate: false,
        },
    },
    mounted() {
        if (this.isLoggedIn && this.user) {
            setTimeout(() => {
                this.showWelcome();
            }, 300);
        }
    },
    beforeDestroy() {
        if (this.typingTimer) {
            clearInterval(this.typingTimer);
            this.typingTimer = null;
        }
        if (this.messageTimer) {
            clearTimeout(this.messageTimer);
            this.messageTimer = null;
        }
    },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;600;700&family=Caveat:wght@400;600;700&display=swap");
.welcome-transition-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 50%, #f0f4f8 100%);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999999;
    overflow: hidden;
}

.welcome-transition-overlay::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at center, rgba(236, 75, 29, 0.05) 0%, transparent 70%);
    animation: backgroundPulse 3s ease-in-out infinite;
}

.welcome-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
    position: relative;
    z-index: 1;
}

.logo-container {
    margin-bottom: 50px;
    animation: logoReveal 1s cubic-bezier(0.16, 1, 0.3, 1);
}

.welcome-logo {
    width: 180px;
    height: auto;
    filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1));
    animation: logoFloat 2s ease-in-out infinite;
}

.messages-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.welcome-title {
    font-size: 72px;
    font-weight: 600;
    margin: 0;
    text-align: center;
    font-family: "Dancing Script", "Caveat", cursive;
    min-height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.typing-text {
    display: inline-block;
}

.cursor {
    display: inline-block;
    animation: blink 1s infinite;
    margin-left: 4px;
    color: #ec4b1d;
    font-weight: 300;
}

.welcome-message {
    font-size: 64px;
    font-weight: 600;
    margin: 0;
    text-align: center;
    font-family: "Dancing Script", "Caveat", cursive;
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
}

.colored-text {
    background: linear-gradient(135deg, #ec4b1d 0%, #ff6b35 25%, #f7931e 50%, #ffa500 75%, #ff8c00 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    background-size: 200% 200%;
    animation: gradientShift 3s ease infinite;
}

@keyframes blink {
    0%,
    50% {
        opacity: 1;
    }
    51%,
    100% {
        opacity: 0;
    }
}

@keyframes gradientShift {
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
}

@keyframes backgroundPulse {
    0%,
    100% {
        transform: scale(1);
        opacity: 0.5;
    }
    50% {
        transform: scale(1.1);
        opacity: 0.8;
    }
}

@keyframes logoReveal {
    0% {
        opacity: 0;
        transform: scale(0.8) translateY(30px);
    }
    100% {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@keyframes logoFloat {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-10px);
    }
}

@keyframes titleReveal {
    0% {
        opacity: 0;
        transform: translateY(20px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes messageReveal {
    0% {
        opacity: 0;
        transform: translateY(15px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

.welcome-fade-enter-active {
    transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.welcome-fade-leave-active {
    transition: opacity 0.5s cubic-bezier(0.4, 0, 1, 1);
}

.welcome-fade-enter,
.welcome-fade-leave-to {
    opacity: 0;
}

@media (max-width: 768px) {
    .welcome-logo {
        width: 140px;
    }

    .welcome-title {
        font-size: 56px;
        min-height: 70px;
    }

    .welcome-message {
        font-size: 48px;
        min-height: 60px;
    }

    .logo-container {
        margin-bottom: 40px;
    }
}

@media (max-width: 480px) {
    .welcome-logo {
        width: 120px;
    }

    .welcome-title {
        font-size: 48px;
        min-height: 60px;
    }

    .welcome-message {
        font-size: 40px;
        min-height: 50px;
    }

    .logo-container {
        margin-bottom: 30px;
    }
}
</style>

